import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Container from '../components/common/Container';
import { ImagePreview } from '../components/common/ImageModal';
import FlowArrow, { StepConnector } from '../components/common/FlowArrow';
import MixedLessonPreview from '../components/lesson/MixedLessonPreview';
import MixedLesson from '../components/lesson/MixedLesson';
import InteractiveModal from '../components/lesson/InteractiveModal';
import CurriculumMapPreview from '../components/curriculum/CurriculumMapPreview';
import CurriculumMapModal from '../components/curriculum/CurriculumMapModal';
import DataCollectionPreview from '../components/data/DataCollectionPreview';
import DataCollectionModal from '../components/data/DataCollectionModal';
import AdaptivePathwayPreview from '../components/pathway/AdaptivePathwayPreview';
import AdaptivePathwayModal from '../components/pathway/AdaptivePathwayModal';
import {
  ChartBarIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  BoltIcon,
  Square3Stack3DIcon,
  QuestionMarkCircleIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

const HowItWorks: React.FC = () => {
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isCurriculumModalOpen, setIsCurriculumModalOpen] = useState(false);
  const [isDataModalOpen, setIsDataModalOpen] = useState(false);
  const [isPathwayModalOpen, setIsPathwayModalOpen] = useState(false);

  // The core platform flow: Curriculum -> Questions -> Data -> Pathways
  const platformFlow = [
    {
      number: '01',
      title: 'Curriculum Mapping',
      subtitle: 'Building the Knowledge Tree',
      description: 'We start by mapping the entire Australian curriculum into an interconnected knowledge graph.',
      details: [
        'Every curriculum outcome becomes a trackable node',
        'Prerequisite relationships are mapped between concepts',
        'Multiple subjects integrated into one unified structure',
        'Year levels K-12 fully covered',
      ],
      icon: Square3Stack3DIcon,
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
      visualLabel: 'Knowledge Tree',
      visualDescription: 'Interconnected curriculum nodes showing prerequisite relationships',
      useCurriculumPreview: true,
    },
    {
      number: '02',
      title: 'Question Artefacts',
      subtitle: 'Turning Curriculum into Content',
      description: 'Each curriculum node is populated with carefully crafted question artefacts designed to assess and build mastery.',
      details: [
        'Questions mapped directly to curriculum outcomes',
        'Multiple question types for varied assessment',
        'Difficulty levels calibrated to each concept',
        'Hints and explanations support learning',
      ],
      icon: QuestionMarkCircleIcon,
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
      visualLabel: 'Question Artefacts',
      visualDescription: 'Sample questions with difficulty indicators and curriculum tags',
      useInteractivePreview: true,
    },
    {
      number: '03',
      title: 'Data Collection',
      subtitle: 'Every Interaction Informs',
      description: 'As students engage with questions, every response generates valuable data that updates their knowledge profile.',
      details: [
        'Response accuracy tracked per curriculum node',
        'Time-to-answer indicates confidence levels',
        'Patterns reveal strengths and gaps',
        'Mastery decay modelled over time',
      ],
      icon: CircleStackIcon,
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
      visualLabel: 'Student Dashboard',
      visualDescription: 'Real-time mastery tracking across curriculum nodes',
      useDataPreview: true,
    },
    {
      number: '04',
      title: 'Adaptive Pathways',
      subtitle: 'Personalised Learning Journeys',
      description: 'The collected data drives intelligent decisions about what each student should learn next.',
      details: [
        'AI selects optimal next questions',
        'Weak areas receive targeted reinforcement',
        'Spaced repetition prevents forgetting',
        'Difficulty adjusts to maintain engagement',
      ],
      icon: ArrowTrendingUpIcon,
      gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]',
      visualLabel: 'Learning Pathway',
      visualDescription: 'Personalised content recommendations based on mastery data',
      usePathwayPreview: true,
    },
  ];

  const learningCycle = [
    {
      phase: 'Diagnose',
      description: 'Students can join at any year level. Initial diagnostic assessments establish their baseline knowledge and identify gaps',
      icon: ClipboardDocumentCheckIcon,
    },
    {
      phase: 'Engage',
      description: 'Interactive lessons capture attention with multimedia content and gamification elements',
      icon: BoltIcon,
    },
    {
      phase: 'Practice',
      description: 'Adaptive questions adjust difficulty based on performance in real-time',
      icon: AcademicCapIcon,
    },
    {
      phase: 'Assess',
      description: 'Continuous formative assessment updates mastery levels instantly',
      icon: CheckCircleIcon,
    },
    {
      phase: 'Reinforce',
      description: 'Spaced repetition schedules reviews based on forgetting curves',
      icon: ArrowPathIcon,
    },
  ];

  const dataFlow = [
    {
      source: 'Student Activity',
      destination: 'Knowledge Graph',
      description: 'Every interaction updates mastery tracking',
    },
    {
      source: 'Knowledge Graph',
      destination: 'AI Engine',
      description: 'Current state informs next question selection',
    },
    {
      source: 'AI Engine',
      destination: 'Content Delivery',
      description: 'Personalized content served in real-time',
    },
    {
      source: 'Performance Data',
      destination: 'All Dashboards',
      description: 'Live updates to all stakeholder views',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              How Lumen8 <span className="text-gradient">Works</span>
            </h1>
            <p className="text-xl text-[var(--color-light-gray)]">
              From curriculum to personalised learning — see how every interaction builds towards mastery.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Platform Flow - The Core "How It Works" */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              The Lumen8 <span className="text-gradient">Method</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              A complete system that transforms curriculum into personalised, adaptive learning experiences
            </p>
          </div>

          <div className="space-y-20">
            {platformFlow.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Content Side */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.gradient} p-4 flex items-center justify-center`}>
                        <step.icon className="w-full h-full text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-[var(--color-electric-blue)]">Step {step.number}</div>
                        <h3 className="text-3xl font-bold text-white">{step.title}</h3>
                        <p className="text-[var(--color-medium-gray)]">{step.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-lg text-[var(--color-light-gray)] mb-8">
                      {step.description}
                    </p>
                    <div className="space-y-3">
                      {step.details.map((detail) => (
                        <div key={detail} className="flex items-start gap-3">
                          <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                          <span className="text-[var(--color-light-gray)]">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Visual Placeholder Side */}
                  <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="card-premium overflow-hidden">
                      {step.useCurriculumPreview ? (
                        <div className="flex items-center justify-center p-6 min-h-[300px]">
                          <CurriculumMapPreview onClick={() => setIsCurriculumModalOpen(true)} />
                        </div>
                      ) : step.useInteractivePreview ? (
                        <div className="flex items-center justify-center p-8 min-h-[300px]">
                          <MixedLessonPreview onClick={() => setIsLessonModalOpen(true)} />
                        </div>
                      ) : step.useDataPreview ? (
                        <div className="flex items-center justify-center p-6 min-h-[300px]">
                          <DataCollectionPreview onClick={() => setIsDataModalOpen(true)} />
                        </div>
                      ) : step.usePathwayPreview ? (
                        <div className="flex items-center justify-center p-6 min-h-[300px]">
                          <AdaptivePathwayPreview onClick={() => setIsPathwayModalOpen(true)} />
                        </div>
                      ) : (
                        <ImagePreview
                          title={step.visualLabel}
                          description={step.visualDescription}
                          imageAlt={step.visualLabel}
                          gradient={step.gradient}
                          icon={step.icon}
                        />
                      )}
                    </div>
                    <div className={`absolute -top-4 -right-4 w-32 h-32 bg-gradient-to-br ${step.gradient} rounded-full filter blur-3xl opacity-20`}></div>
                  </div>
                </div>

                {/* Arrow connector between steps */}
                {index < platformFlow.length - 1 && (
                  <div className="flex justify-center mt-12">
                    <StepConnector />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Learning Cycle */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              The Learning <span className="text-gradient">Cycle</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Starting with diagnostic assessment, followed by engagement, practice, assessment, and reinforcement
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {learningCycle.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-6 h-full text-center hover-glow">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-4">
                    <phase.icon className="w-full h-full text-white" />
                  </div>
                  <div className="text-2xl font-bold text-white mb-2">{index + 1}. {phase.phase}</div>
                  <p className="text-[var(--color-light-gray)]">{phase.description}</p>
                  {index < learningCycle.length - 1 && (
                    <div className="mt-4 hidden lg:flex justify-center">
                      <FlowArrow direction="right" size="sm" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Data Flow Visualization */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Real-Time Data <span className="text-gradient">Flow</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Every interaction updates the system instantly
            </p>
          </div>

          <div className="card-premium p-12 max-w-4xl mx-auto">
            <div className="space-y-8">
              {dataFlow.map((flow, index) => (
                <motion.div
                  key={flow.source}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-6"
                >
                  <div className="flex-1 text-right">
                    <div className="text-lg font-semibold text-white">{flow.source}</div>
                  </div>
                  <FlowArrow direction="right" size="md" />
                  <div className="flex-1">
                    <div className="text-lg font-semibold text-white">{flow.destination}</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">{flow.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Getting Started Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Getting <span className="text-gradient">Started</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Whether you're a school, parent, or education provider — onboarding is simple
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <AcademicCapIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">1. Create Account</h3>
                <p className="text-[var(--color-light-gray)]">
                  Sign up and set up your profile. Add students, connect with teachers, or join your school.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-4 mx-auto mb-6">
                  <ChartBarIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">2. Baseline Assessment</h3>
                <p className="text-[var(--color-light-gray)]">
                  A short diagnostic helps establish where each student is across the curriculum.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <ArrowTrendingUpIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">3. Start Learning</h3>
                <p className="text-[var(--color-light-gray)]">
                  Personalised learning begins immediately, adapting to each student's unique needs.
                </p>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-black text-white mb-6">
              Ready to See It <span className="text-gradient">In Action?</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Experience how Lumen8 transforms curriculum into personalised learning journeys.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Schedule Demo
                <ArrowRightIcon className="w-5 h-5" />
              </a>
              <a href="/features" className="btn-glass text-lg px-8 py-4">
                Explore Features
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Interactive Lesson Modal */}
      <InteractiveModal
        isOpen={isLessonModalOpen}
        onClose={() => setIsLessonModalOpen(false)}
      >
        <MixedLesson onClose={() => setIsLessonModalOpen(false)} />
      </InteractiveModal>

      {/* Curriculum Map Modal */}
      <CurriculumMapModal
        isOpen={isCurriculumModalOpen}
        onClose={() => setIsCurriculumModalOpen(false)}
      />

      {/* Data Collection Modal */}
      <DataCollectionModal
        isOpen={isDataModalOpen}
        onClose={() => setIsDataModalOpen(false)}
      />

      {/* Adaptive Pathway Modal */}
      <AdaptivePathwayModal
        isOpen={isPathwayModalOpen}
        onClose={() => setIsPathwayModalOpen(false)}
      />
    </>
  );
};

export default HowItWorks;